import { Typography } from 'antd';
import axios from 'axios';
import type { ReactNode } from 'react';
import type { ApiErrorResponse } from '@tms/shared';

import i18n from '@/i18n';

import { errorMessage } from './client';

/**
 * Идентификатор запроса из ответа сервера — тот же, что пишется в лог сервера
 * и в журнал действий (см. TenantMiddleware и AllExceptionsFilter в API).
 *
 * Отдаётся только для сбоев на стороне сервера (5xx). У ошибок, которые
 * пользователь исправляет сам («в ёмкости недостаточно топлива», «код уже
 * занят»), текст и есть вся нужная информация, и технический номер под ним
 * только мешал бы читать.
 */
function supportRequestId(error: unknown): string | null {
  if (!axios.isAxiosError<ApiErrorResponse>(error)) return null;

  const response = error.response;
  if (!response || response.status < 500) return null;

  const fromHeader: unknown = response.headers['x-request-id'];
  return response.data?.requestId ?? (typeof fromHeader === 'string' ? fromHeader : null);
}

/**
 * Сообщение об ошибке для message.error().
 *
 * При сбое сервера под текстом мелко выводится код обращения с кнопкой
 * копирования: по нему в логах находится ровно эта ошибка, а не все ошибки
 * за день. Раньше, чтобы назвать его поддержке, пришлось бы открывать
 * инструменты разработчика в браузере.
 */
export function errorNotice(error: unknown): { content: ReactNode; duration: number } {
  const text = errorMessage(error);
  const requestId = supportRequestId(error);

  if (!requestId) return { content: text, duration: 3 };

  return {
    content: (
      <span style={{ display: 'inline-block', textAlign: 'left', verticalAlign: 'top' }}>
        {text}
        <br />
        <Typography.Text type="secondary" copyable={{ text: requestId }} style={{ fontSize: 11 }}>
          {i18n.t('Код обращения')}: {requestId}
        </Typography.Text>
      </span>
    ),
    // Дольше обычного: код нужно успеть прочитать или скопировать. Пока курсор
    // на сообщении, оно не исчезает вовсе.
    duration: 10,
  };
}
